import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { autoMobileInvoiceService } from '../../../../services/autoMobileInvoiceService';
import { autoMobileVehicleService } from '../../../../services/autoMobileVehicleService';
import { productService } from '../../../../services/productService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, FileText, Plus, Trash2, AlertCircle } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';
import '../AutoMobilePage.css';

interface LineForm {
  lineType: 'Part' | 'Labor' | 'Other';
  productId: number | null;
  description: string;
  quantity: number;
  unitPrice: number;
}

const blankLine = (): LineForm => ({ lineType: 'Part', productId: null, description: '', quantity: 1, unitPrice: 0 });

const InvoiceCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();
  const isEdit = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  const [vehicleId, setVehicleId] = useState(0);
  const [jobType, setJobType] = useState<string>('Repair');
  const [mileageAtService, setMileageAtService] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineForm[]>([blankLine()]);

  useEffect(() => {
    autoMobileVehicleService.getAll().then(res => {
      const d = (res.data as any)?.data ?? [];
      setVehicles(Array.isArray(d) ? d : []);
    }).catch(() => {});
    productService.getProducts({ pageNumber: 1, pageSize: 500 }).then(res => {
      const d = (res.data as any)?.data ?? [];
      setProducts(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingData(true);
    autoMobileInvoiceService.getById(parseInt(id))
      .then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setVehicleId(d.vehicleId ?? 0);
        setJobType(d.jobType ?? 'Repair');
        setMileageAtService(d.mileageAtService ?? 0);
        setTaxAmount(d.taxAmount ?? 0);
        setDiscountAmount(d.discountAmount ?? 0);
        setNotes(d.notes ?? '');
        setLines((d.lines ?? []).map((l: any) => ({
          lineType: l.lineType ?? 'Part',
          productId: l.productId ?? null,
          description: l.description ?? '',
          quantity: l.quantity ?? 1,
          unitPrice: l.unitPrice ?? 0,
        })) || [blankLine()]);
      })
      .catch(() => setError(t('automobile.invoice.failedToLoad')))
      .finally(() => setLoadingData(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateLine = (idx: number, patch: Partial<LineForm>) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const addLine = () => setLines(prev => [...prev, blankLine()]);
  const removeLine = (idx: number) => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const pickProduct = (idx: number, productId: number) => {
    const p = products.find(pr => pr.id === productId);
    updateLine(idx, { productId, description: p ? p.name : lines[idx].description });
  };

  const subTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const total = subTotal + Number(taxAmount) - Number(discountAmount);

  const validate = () => {
    if (lines.some(l => !l.description.trim() || l.quantity <= 0)) {
      setError(t('automobile.invoice.lineValidation'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSaving(true);
    const payload = {
      vehicleId: vehicleId || null,
      jobType,
      mileageAtService,
      taxAmount: Number(taxAmount),
      discountAmount: Number(discountAmount),
      notes: notes.trim() || undefined,
      lines: lines.map(l => ({
        lineType: l.lineType,
        productId: l.lineType === 'Part' ? l.productId ?? undefined : undefined,
        description: l.description.trim(),
        quantity: l.quantity,
        unitPrice: l.unitPrice,
      })),
    };
    try {
      if (isEdit && id) await autoMobileInvoiceService.update(parseInt(id), payload);
      else await autoMobileInvoiceService.create(payload);
      navigate('/automobile/invoices');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('automobile.invoice.failedToSave'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page auto-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="auto-create-page">
        <div className="ims-create-body-page">

          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/automobile/invoices')}>
                <ArrowLeft size={13} /> {t('automobile.invoice.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('automobile.invoice.editTitle') : t('automobile.invoice.createTitle')}
                </h1>
                <p className="ims-page-subtitle">{t('automobile.invoice.createSubtitle')}</p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/automobile/invoices')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <FileText size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('automobile.invoice.details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.invoice.vehicle')}</label>
                      <select className="mi-input" value={vehicleId} onChange={e => setVehicleId(Number(e.target.value))} disabled={isEdit}>
                        <option value={0}>{t('automobile.invoice.selectVehicle')}</option>
                        {vehicles.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.registrationNumber} — {v.make} {v.model} ({v.customerName})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.invoice.jobType')}</label>
                      <select className="mi-input" value={jobType} onChange={e => setJobType(e.target.value)}>
                        <option value="Repair">{t('automobile.invoice.jobTypes.Repair')}</option>
                        <option value="Service">{t('automobile.invoice.jobTypes.Service')}</option>
                        <option value="Maintenance">{t('automobile.invoice.jobTypes.Maintenance')}</option>
                        <option value="Inspection">{t('automobile.invoice.jobTypes.Inspection')}</option>
                        <option value="Other">{t('automobile.invoice.jobTypes.Other')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.invoice.mileageAtService')}</label>
                      <input type="number" className="mi-input" value={mileageAtService}
                        onChange={e => setMileageAtService(Number(e.target.value))} min={0} />
                    </div>
                    <div />
                  </div>
                  <div className="mi-field">
                    <label className="mi-label">{t('common.notes')}</label>
                    <textarea className="mi-input mi-textarea" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} />
                  </div>
                </div>
              </div>

              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header" style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <FileText size={14} className="mi-header-icon" />
                    <span className="ims-card-title">{t('automobile.invoice.lines')}</span>
                  </span>
                  <button type="button" className="mi-add-btn" onClick={addLine}>
                    <Plus size={13} /> {t('automobile.invoice.addLine')}
                  </button>
                </div>
                <div className="ims-card-body" style={{ padding: 0 }}>
                  <table className="auto-lines-table">
                    <thead>
                      <tr>
                        <th style={{ width: '110px' }}>{t('automobile.invoice.lineType')}</th>
                        <th style={{ width: '200px' }}>{t('automobile.invoice.part')}</th>
                        <th>{t('automobile.invoice.description')}</th>
                        <th className="num" style={{ width: '80px' }}>{t('automobile.invoice.qty')}</th>
                        <th className="num" style={{ width: '110px' }}>{t('automobile.invoice.unitPrice')}</th>
                        <th className="num" style={{ width: '110px' }}>{t('automobile.invoice.lineTotal')}</th>
                        <th style={{ width: '40px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l, idx) => (
                        <tr key={idx}>
                          <td>
                            <select className="mi-recipe-sel" value={l.lineType}
                              onChange={e => updateLine(idx, { lineType: e.target.value as LineForm['lineType'], productId: e.target.value === 'Part' ? l.productId : null })}>
                              <option value="Part">{t('automobile.invoice.lineTypes.Part')}</option>
                              <option value="Labor">{t('automobile.invoice.lineTypes.Labor')}</option>
                              <option value="Other">{t('automobile.invoice.lineTypes.Other')}</option>
                            </select>
                          </td>
                          <td>
                            {l.lineType === 'Part' ? (
                              <select className="mi-recipe-sel" value={l.productId ?? 0}
                                onChange={e => pickProduct(idx, Number(e.target.value))}>
                                <option value={0}>{t('automobile.invoice.selectPart')}</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            ) : '—'}
                          </td>
                          <td>
                            <input type="text" className="mi-recipe-inp" value={l.description}
                              onChange={e => updateLine(idx, { description: e.target.value })} maxLength={200} />
                          </td>
                          <td className="num">
                            <input type="number" className="mi-recipe-inp" style={{ textAlign: 'right' }} value={l.quantity} min={0.01} step={0.01}
                              onChange={e => updateLine(idx, { quantity: Number(e.target.value) })} />
                          </td>
                          <td className="num">
                            <input type="number" className="mi-recipe-inp" style={{ textAlign: 'right' }} value={l.unitPrice} min={0} step={0.01}
                              onChange={e => updateLine(idx, { unitPrice: Number(e.target.value) })} />
                          </td>
                          <td className="num" style={{ fontWeight: 600 }}>{fmt(l.quantity * l.unitPrice)}</td>
                          <td>
                            <button type="button" className="mi-del-btn" onClick={() => removeLine(idx)}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="auto-lines-totals">
                    <div className="auto-lines-totals-row">
                      <span>{t('automobile.invoice.subTotal')}</span><span>{fmt(subTotal)}</span>
                    </div>
                    <div className="auto-lines-totals-row">
                      <span>{t('automobile.invoice.tax')}</span>
                      <input type="number" className="mi-recipe-inp" style={{ width: 100, textAlign: 'right' }} value={taxAmount} min={0} step={0.01}
                        onChange={e => setTaxAmount(Number(e.target.value))} />
                    </div>
                    <div className="auto-lines-totals-row">
                      <span>{t('automobile.invoice.discount')}</span>
                      <input type="number" className="mi-recipe-inp" style={{ width: 100, textAlign: 'right' }} value={discountAmount} min={0} step={0.01}
                        onChange={e => setDiscountAmount(Number(e.target.value))} />
                    </div>
                    <div className="auto-lines-totals-row grand">
                      <span>{t('automobile.invoice.total')}</span><span>{fmt(total)}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default InvoiceCreatePage;
